import { useCallback, useContext } from 'react';

import resumeCardToChatButtonClick, {
    type FolderName,
    type HhtmSource,
} from '@hh.ru/analytics-js-events/build/xhh/employer/responses/resume_card/resume_card_to_chat_button_click';
import { ChatikContext } from '@hh.ru/chatik-integration';
import { useBreakpoint, Button } from '@hh.ru/magritte-ui';
import { BubbleRoundOutlinedSize16 } from '@hh.ru/magritte-ui/icon';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import { ResumeCardActionsContext } from 'src/components/ResumeCard/Actions/Context';
import translation from 'src/components/translation';
import { useSelector } from 'src/hooks/useSelector';
import { EmployerStateExtName } from 'src/models/negotiationTopic.types';

const TrlKeys = {
    chat: 'resume.card.action.chat',
    messages: 'resume.card.action.messages',
};

interface Props {
    chatId: number;
    conversationMessagesCount: number;
    conversationUnreadByEmployerCount: number;
    lastEmployerStateExtName: EmployerStateExtName;
    topicId?: string | number;
    vacancyId: string | number;
    isMobile?: boolean;
}

const ChatikButton: TranslatedComponent<Props> = ({
    chatId,
    conversationMessagesCount,
    conversationUnreadByEmployerCount,
    lastEmployerStateExtName,
    topicId,
    vacancyId,
    isMobile,
    trls,
}) => {
    const openChatik = useContext(ChatikContext)?.openChatik;
    const { commentToggle, isNew, markAsRead, messagesToggle, handleContactClick } =
        useContext(ResumeCardActionsContext);

    const hhtmSource = useSelector((state) => state.analyticsParams.hhtmSource) as HhtmSource;
    const { isGtM } = useBreakpoint();
    const [, , setComments] = commentToggle;
    const [, setToggleDialogue] = messagesToggle;

    const handleClick = useCallback(() => {
        if (topicId && lastEmployerStateExtName) {
            resumeCardToChatButtonClick({
                hhtmSource,
                vacancyId: vacancyId.toString(),
                topicId: vacancyId.toString(),
                folderName: lastEmployerStateExtName as FolderName,
                ...(conversationMessagesCount
                    ? {
                          state: conversationUnreadByEmployerCount ? 'has_new_message' : 'all_read',
                          unreadMessageCount: conversationUnreadByEmployerCount,
                          messageCount: conversationMessagesCount,
                      }
                    : {
                          state: 'no_message',
                      }),
            });
        }
        isNew && markAsRead?.();
        handleContactClick?.();
        if (conversationMessagesCount) {
            setToggleDialogue();
            setComments(false);
        } else {
            openChatik?.({ chatId, view: 'compact', hhtmFromLabel: 'employer_vacancy_responses_chat_link' });
        }
    }, [
        topicId,
        lastEmployerStateExtName,
        isNew,
        markAsRead,
        setToggleDialogue,
        setComments,
        openChatik,
        chatId,
        hhtmSource,
        vacancyId,
        conversationUnreadByEmployerCount,
        conversationMessagesCount,
        handleContactClick,
    ]);

    const hasMessages = conversationMessagesCount > 0;
    const hasUnreadMessages = hasMessages && conversationUnreadByEmployerCount > 0;
    const messagesCount = conversationUnreadByEmployerCount || conversationMessagesCount;

    return (
        <Button
            stretched={isMobile}
            size="small"
            mode={hasUnreadMessages ? 'primary' : 'secondary'}
            icon={<BubbleRoundOutlinedSize16 />}
            postfix={hasMessages ? `${messagesCount > 99 ? '99+' : messagesCount}` : undefined}
            onClick={handleClick}
            aria-label={trls[TrlKeys.chat]}
            data-qa={hasUnreadMessages ? 'show-unread-resume-messages' : 'show-resume-messages'}
        >
            {(isMobile || isGtM) && trls[TrlKeys.chat]}
        </Button>
    );
};

export default translation(ChatikButton);
